import React, { useMemo } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  useTheme,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Button,
  Chip,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import SyncIcon from "@mui/icons-material/Sync";
import AddIcon from "@mui/icons-material/Add";
import moment from "moment/moment";
import { formatValue, calculateAndFormatNoAbsInt } from "../../component/Methods";

const AccountingTable = ({
  accountingData,
  searchTerm,
  onSearchChange,
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
  onEdit,
  onSync,
  onAdd,
}) => {
  const theme = useTheme();

  const columns = [
    {
      field: "abbreviation",
      headerName: "Entity Abbreviation",
      flex: 1.5,
      minWidth: 50,
    },
    {
      field: "name",
      headerName: "Entity Name",
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: "isSMA",
      headerName: "Entity Type",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const isSMA = params.value === true;
        return (
          <Chip
            label={isSMA ? "SMA" : "Fund"}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: "0.75rem",
              color: isSMA
                ? theme.palette.mode === 'dark' ? '#90caf9' : '#1565c0'
                : theme.palette.mode === 'dark' ? '#81c784' : '#2e7d32',
              border: `1px solid ${isSMA 
                ? theme.palette.mode === 'dark' ? '#42a5f5' : '#bbdefb'
                : theme.palette.mode === 'dark' ? '#66bb6a' : '#c8e6c9'}`,
            }}
          />
        );
      },
    },
    {
      field: "aum",
      headerName: "AUM",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => {
        //handle if aum is undefined or null
        if (params.value === undefined || params.value === null) {
          return "0.00";
        }
        return formatValue(params.value);
      },
    },
    {
      field: "date",
      headerName: "Creation Date",
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => {
        return value ? moment(value).format("MMM DD, YYYY") : "-";
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Edit">
            <IconButton
              color="primary"
              size="small"
              onClick={() => onEdit && onEdit(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const filteredData = useMemo(() => {
    let data = accountingData || [];
    
    // Apply search filter
    if (searchTerm) {
      data = data.filter((item) =>
        item.abbreviation?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Sort by fund name in ascending order (default behavior)
    data = [...data].sort((a, b) => {
      const aValue = a.abbreviation || '';
      const bValue = b.abbreviation || '';
      return aValue.localeCompare(bValue);
    });
    
    return data;
  }, [accountingData, searchTerm]);

  // Add hasStrategyData flag for expandability
  const rowsWithExpandInfo = useMemo(() => {
    return filteredData.map((item) => ({
      ...item,
      hasStrategyData: (item?.strategyAUMs || [])?.length > 0,
    }));
  }, [filteredData]);

  const getDetailPanelContent = ({ row }) => {
    if (!row.hasStrategyData) return null;

    return (
      <Box sx={{ p: 2, backgroundColor: theme.palette.grey[50] }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Strategy Name</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: "right" }}>Strategy AUM</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(row?.strategyAUMs || []).map((strategy, index) => (
              <TableRow
                key={strategy.strategyId || index}
                sx={{
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <TableCell>{strategy?.strategyName || "-"}</TableCell>
                <TableCell sx={{ textAlign: "right" }}>
                  {strategy?.strategyAUM < 0
                    ? `(${calculateAndFormatNoAbsInt(Math.abs(strategy?.strategyAUM))})`
                    : calculateAndFormatNoAbsInt(strategy?.strategyAUM)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  const getDetailPanelHeight = ({ row }) => {
    if (!row.hasStrategyData) return 0;
    // Base height + (number of strategies * row height) + padding
    return 120 + (row?.strategyAUMs?.length || 0) * 40;
  };

  return (
    <Box sx={{  width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search accounting records..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 300,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />
        </Box>
        
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {filteredData.length} records
          </Typography>
          
          <Button
            variant="contained"
            startIcon={<SyncIcon />}
            onClick={onSync}
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              fontWeight: 600,
              minWidth: 120,
            }}
          >
            Sync Data
          </Button>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAdd}
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              fontWeight: 600,
              minWidth: 120,
              backgroundColor: theme.palette.success.main,
              "&:hover": {
                backgroundColor: theme.palette.success.dark,
              },
            }}
          >
            Add Item
          </Button>
        </Box>
      </Box>


      <DataGridPro
        rows={rowsWithExpandInfo}
        columns={columns}
        pageSize={1000}
        onPageSizeChange={(newPageSize) => onPageSizeChange(newPageSize)}
        rowsPerPageOptions={[25, 50, 100, 500, 1000, 2000]}
        pagination
        disableSelectionOnClick
        density="compact"
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
        isDetailPanelToggleField={(field) => field === "hasStrategyData"}
        sx={{
          border: "none",
          height: "calc(100vh - 20px)",
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
            fontWeight: 500,
           
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.grey[100],
            borderBottom: `2px solid ${theme.palette.divider}`,
          },
          "& .MuiDataGrid-columnHeader": {
            fontWeight: 600,
            color: theme.palette.text.primary,
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: "transparent",
          },
          "& .MuiDataGrid-detailPanel": {
            backgroundColor: "transparent",
          },
        }}
      />
    </Box>
  );
};

export default AccountingTable;